'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const modules = [
{
  id: '01',
  title: 'Balayage Fundamentals',
  category: 'Color',
  lessons: 18,
  avgTime: '38 min',
  level: 'Beginner',
  levelClass: 'badge-beginner',
  techniques: 6,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_14b558d89-1772182366865.png",
  alt: 'Stylist applying balayage color technique on long hair in professional salon',
  highlight: false,
  span: 'md:col-span-1'
},
{
  id: '02',
  title: 'Advanced Foil Systems',
  category: 'Highlights',
  lessons: 24,
  avgTime: '45 min',
  level: 'Intermediate',
  levelClass: 'badge-intermediate',
  techniques: 9,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_16679ed18-1772076086464.png",
  alt: 'Professional hairstylist carefully placing foils during highlighting treatment',
  highlight: true,
  span: 'md:col-span-1'
},
{
  id: '03',
  title: 'Nail Art Architecture',
  category: 'Nail',
  lessons: 31,
  avgTime: '29 min',
  level: 'All Levels',
  levelClass: 'badge-beginner',
  techniques: 14,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2f55dc4-1766164495536.png",
  alt: 'Close-up of intricate nail art design being painted with fine brush on pink nail',
  highlight: false,
  span: 'md:col-span-1'
},
{
  id: '04',
  title: 'Skin Prep & Canvas',
  category: 'Skincare',
  lessons: 15,
  avgTime: '22 min',
  level: 'Beginner',
  levelClass: 'badge-beginner',
  techniques: 5,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_141352a60-1772182368136.png",
  alt: 'Esthetician applying skincare preparation treatment on client face in clean salon',
  highlight: false,
  span: 'md:col-span-1'
},
{
  id: '05',
  title: 'Microblading Mastery',
  category: 'Brows',
  lessons: 22,
  avgTime: '55 min',
  level: 'Advanced',
  levelClass: 'badge-advanced',
  techniques: 8,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_198c00da4-1766797752996.png",
  alt: 'Beauty technician performing microblading eyebrow procedure with precision tool',
  highlight: false,
  span: 'md:col-span-1'
},
{
  id: '06',
  title: 'Lash Extension Systems',
  category: 'Lashes',
  lessons: 19,
  avgTime: '48 min',
  level: 'Intermediate',
  levelClass: 'badge-intermediate',
  techniques: 7,
  img: "https://images.unsplash.com/photo-1589710751893-f9a6770ad71b",
  alt: 'Close-up of lash extension application process showing individual lash placement',
  highlight: false,
  span: 'md:col-span-1'
}];


const stats = [
{ value: '12', unit: 'modules', label: 'Core tracks' },
{ value: '847', unit: 'techniques', label: 'Step-by-step' },
{ value: '4.2h', unit: 'avg/module', label: 'Completion time' },
{ value: '98%', unit: 'pass rate', label: 'Certification exam' }];


export default function CurriculumSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="curriculum" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="section-divider mb-24" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="reveal">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-DEFAULT mb-3 block">
              § Curriculum
            </span>
            <h2 className="font-sans text-4xl sm:text-5xl font-extrabold text-frost tracking-tight leading-tight">
              Built module by module.<br />
              <span className="font-display italic font-light text-chrome">
                Engineered for speed.
              </span>
            </h2>
          </div>
          <div className="reveal flex flex-col gap-1 md:text-right">
            <p className="text-chrome text-sm font-light max-w-xs">
              Every lesson spec'd with camera angle, technique count, and average completion time.
            </p>
          </div>
        </div>

        {/* Stats bar */}
        <div className="reveal grid grid-cols-2 sm:grid-cols-4 gap-px bg-frost/5 rounded-2xl overflow-hidden mb-16 border border-frost/8">
          {stats?.map((s, i) =>
          <div key={i} className="glass-card p-6 text-center spec-row">
              <div className="text-3xl font-extrabold gradient-text-sky mb-1">{s?.value}</div>
              <div className="text-frost text-sm font-semibold mb-0.5">{s?.unit}</div>
              <div className="text-chrome-dim text-xs uppercase tracking-wider">{s?.label}</div>
            </div>
          )}
        </div>

        {/* Bento grid — varied sizes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger-children">
          {modules?.map((mod, i) =>
          <div
            key={mod?.id}
            className={`reveal ${mod?.span} glass-card rounded-2xl overflow-hidden glow-hover transition-all duration-500 group cursor-pointer ${
            mod?.highlight ? 'ring-1 ring-periwinkle/40 shadow-[0_0_32px_rgba(123,97,255,0.15)]' : ''}`
            }>
            
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <AppImage
                src={mod?.img}
                alt={mod?.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-slate-deep via-slate-deep/30 to-transparent" />
                {/* Module number */}
                <div className="absolute top-4 left-4 text-xs font-bold text-chrome-dim tracking-widest">
                  {mod?.id}
                </div>
                {/* Category */}
                <div className="absolute top-4 right-4 glass-card px-2.5 py-1 rounded-full text-[10px] font-bold text-sky-DEFAULT tracking-wide uppercase">
                  {mod?.category}
                </div>
                {mod?.highlight &&
              <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-periwinkle/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-[10px] font-bold text-white tracking-wide">⚡ Most Popular</span>
                  </div>
              }
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="text-frost font-bold text-base leading-tight">{mod?.title}</h3>
                  <span className={`${mod?.levelClass} text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0`}>
                    {mod?.level}
                  </span>
                </div>

                {/* Spec row */}
                <div className="flex items-center gap-4 py-3 border-t border-frost/5">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-sky-DEFAULT" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                      <path d="M14 6a2 2 0 00-2 2v3.586l1.707 1.707A1 1 0 0015 12.586V8a2 2 0 00-1-1.732V6z" />
                    </svg>
                    <span className="text-xs text-chrome font-semibold">{mod?.lessons} lessons</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-chrome-dim" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-chrome-dim">{mod?.avgTime} avg</span>
                  </div>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <svg className="w-3.5 h-3.5 text-periwinkle-DEFAULT" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-xs text-chrome-dim">{mod?.techniques} techniques</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="reveal text-center mt-12">
          <a
            href="#download"
            onClick={(e) => {e?.preventDefault();document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });}}
            className="inline-flex items-center gap-2 text-sky-DEFAULT text-sm font-semibold border-b border-sky-DEFAULT/40 pb-0.5 hover:border-sky-DEFAULT transition-colors">
            
            View all 12 curriculum tracks
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>);

}